import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronRight, FileText, FolderInput, MoreHorizontal } from 'lucide-react'
import type { DocLocationGroup } from '../utils/spaceDocs'
import type { PageDocument } from '@/lib/db/rxdb/hooks/entities/pages'
import type { ProjectDocument } from '@/lib/db/rxdb/schemas/project'
import { buildTree, flattenTree } from '@/routes/features/pages/utils/pageTreeUtils'
import { MoveDocPopover } from '@/routes/features/pages/components/MoveDocPopover'
import { LocationSwatch } from '@/components/app/sidebars/spaces/SpaceSwatch'
import { Icon, normalizeIconName } from '@/components/app/pickers/icon-picker'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useDateTimeFormatters } from '@/lib/i18n/useDateTimeFormatters'
import { cn } from '@/lib/utils'

const GRID = 'grid grid-cols-[minmax(0,1fr)_72px_28px] sm:grid-cols-[minmax(0,1fr)_160px_96px_28px]'
const INDENT = 20
const DAY = 86_400_000

function useUpdatedLabel() {
  const { t } = useTranslation('projects')
  const { date } = useDateTimeFormatters()
  return useCallback(
    (ts: number) => {
      const startOfToday = new Date()
      startOfToday.setHours(0, 0, 0, 0)
      if (ts >= startOfToday.getTime()) return t('docs.allDocs.today')
      if (ts >= startOfToday.getTime() - DAY) return t('docs.allDocs.yesterday')
      const sameYear = new Date(ts).getFullYear() === startOfToday.getFullYear()
      return date(new Date(ts), sameYear ? { month: 'short', day: 'numeric' } : { month: 'short', day: 'numeric', year: 'numeric' })
    },
    [date, t],
  )
}

interface SpaceDocsTableProps {
  space: ProjectDocument
  /** "By location" groups; for "Recent" pass a single flat list instead. */
  groups?: Array<DocLocationGroup>
  recent?: Array<PageDocument>
  locationsById: ReadonlyMap<string, ProjectDocument>
  onOpen: (page: PageDocument) => void
}

/**
 * All docs in a space (design 22a): Title, where it lives, when it changed.
 * By location, space docs come first, then one group per project; rows nest
 * with expand/collapse. Each row's ⋯ has "Move to…".
 */
export function SpaceDocsTable({ space, groups, recent, locationsById, onOpen }: SpaceDocsTableProps) {
  const { t } = useTranslation('projects')
  const [collapsedIds, setCollapsedIds] = useState<Set<string>>(() => new Set())
  const updatedLabel = useUpdatedLabel()

  const toggle = useCallback((id: string) => {
    setCollapsedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  return (
    <div className="overflow-hidden rounded-[14px] border bg-card" role="table" aria-label={t('docs.allDocs.title')}>
      <div role="row" className={cn(GRID, 'h-9 items-center border-b px-4 text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground')}>
        <span role="columnheader">{t('docs.columns.title')}</span>
        <span role="columnheader" className="hidden sm:block">{t('docs.allDocs.livesIn')}</span>
        <span role="columnheader">{t('docs.columns.updated')}</span>
        <span aria-hidden />
      </div>
      {groups
        ? groups.map((group) => (
            <LocationGroup
              key={group.location.id}
              group={group}
              space={space}
              collapsedIds={collapsedIds}
              onToggle={toggle}
              locationsById={locationsById}
              updatedLabel={updatedLabel}
              onOpen={onOpen}
            />
          ))
        : (recent ?? []).map((page) => (
            <DocRow
              key={page.id}
              page={page}
              depth={0}
              hasChildren={false}
              collapsed={false}
              onToggle={toggle}
              location={locationsById.get(page.projectId) ?? null}
              updatedLabel={updatedLabel}
              onOpen={onOpen}
            />
          ))}
      {!groups && (recent ?? []).length === 0 && (
        <p className="px-4 py-8 text-center text-sm text-muted-foreground">{t('docs.empty')}</p>
      )}
    </div>
  )
}

function LocationGroup({
  group,
  space,
  collapsedIds,
  onToggle,
  locationsById,
  updatedLabel,
  onOpen,
}: {
  group: DocLocationGroup
  space: ProjectDocument
  collapsedIds: Set<string>
  onToggle: (id: string) => void
  locationsById: ReadonlyMap<string, ProjectDocument>
  updatedLabel: (ts: number) => string
  onOpen: (page: PageDocument) => void
}) {
  const { t } = useTranslation('projects')
  const tree = useMemo(() => buildTree(group.pages), [group.pages])
  const rows = useMemo(() => flattenTree(tree, 0, collapsedIds), [tree, collapsedIds])
  const isSpace = group.kind === 'space'

  return (
    <div role="rowgroup" data-testid={`docs-group-${group.kind}`}>
      <div role="row" className="flex h-10 items-center gap-2 border-b bg-secondary/50 px-4 text-[13px]">
        <LocationSwatch location={group.location} size={14} />
        <span className="truncate font-semibold">{isSpace ? t('docs.allDocs.spaceDocs') : group.location.name}</span>
        <span className="shrink-0 tabular-nums text-muted-foreground">{group.pages.length}</span>
        <span className="min-w-0 truncate text-[12px] text-muted-foreground">
          {isSpace ? t('docs.allDocs.visibleTo', { name: space.name }) : t('docs.allDocs.projectDocs')}
        </span>
      </div>
      {rows.length === 0 ? (
        <p className="border-b px-4 py-3 text-[13px] text-muted-foreground">{t('docs.allDocs.noSpaceDocs')}</p>
      ) : (
        rows.map(({ node, depth }) => (
          <DocRow
            key={node.page.id}
            page={node.page}
            depth={depth}
            hasChildren={node.children.length > 0}
            collapsed={collapsedIds.has(node.page.id)}
            onToggle={onToggle}
            location={locationsById.get(node.page.projectId) ?? null}
            updatedLabel={updatedLabel}
            onOpen={onOpen}
          />
        ))
      )}
    </div>
  )
}

function DocRow({
  page,
  depth,
  hasChildren,
  collapsed,
  onToggle,
  location,
  updatedLabel,
  onOpen,
}: {
  page: PageDocument
  depth: number
  hasChildren: boolean
  collapsed: boolean
  onToggle: (id: string) => void
  location: ProjectDocument | null
  updatedLabel: (ts: number) => string
  onOpen: (page: PageDocument) => void
}) {
  const { t } = useTranslation('projects')
  const [moveOpen, setMoveOpen] = useState(false)
  const iconName = normalizeIconName(page.icon)
  const title = page.title || t('docs.untitled')

  return (
    <div
      role="row"
      data-testid="all-docs-row"
      className={cn(GRID, 'group/doc h-11 cursor-pointer items-center border-b px-4 text-sm transition-colors last:border-b-0 hover:bg-secondary/40')}
      onClick={() => onOpen(page)}
    >
      <span role="cell" className="flex min-w-0 items-center gap-1.5" style={{ paddingLeft: depth * INDENT }}>
        <button
          type="button"
          aria-label={collapsed ? t('sidebar.expand') : t('sidebar.collapse')}
          aria-expanded={hasChildren ? !collapsed : undefined}
          tabIndex={hasChildren ? 0 : -1}
          onClick={(e) => {
            e.stopPropagation()
            onToggle(page.id)
          }}
          className={cn(
            'flex size-5 shrink-0 items-center justify-center rounded text-muted-foreground hover:bg-secondary',
            !hasChildren && 'invisible',
          )}
        >
          <ChevronRight className={cn('size-3.5 transition-transform motion-reduce:transition-none', !collapsed && 'rotate-90')} />
        </button>
        {iconName ? (
          <Icon name={iconName} className="size-4 shrink-0 text-muted-foreground" />
        ) : (
          <FileText className="size-4 shrink-0 text-muted-foreground" />
        )}
        <span className="truncate font-medium">{title}</span>
      </span>
      <span role="cell" className="hidden min-w-0 sm:block">
        {location && (
          <span className="inline-flex max-w-full items-center gap-1.5 text-[12.5px] text-muted-foreground">
            <LocationSwatch location={location} size={12} />
            <span className="truncate">{location.parentProjectId ? location.name : t('docs.allDocs.space')}</span>
          </span>
        )}
      </span>
      <span role="cell" className="truncate text-[12.5px] text-muted-foreground">{updatedLabel(page.updatedOn)}</span>
      <span role="cell" className="flex justify-end" onClick={(e) => e.stopPropagation()}>
        <MoveDocPopover page={page} open={moveOpen} onOpenChange={setMoveOpen} align="end">
          <span className="inline-flex">
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  aria-label={t('docs.options')}
                  className="flex size-6 items-center justify-center rounded text-muted-foreground opacity-0 transition-opacity hover:bg-secondary hover:text-foreground focus-visible:opacity-100 group-hover/doc:opacity-100 data-[state=open]:opacity-100 [@media(hover:none)]:opacity-100"
                >
                  <MoreHorizontal className="size-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-44" onCloseAutoFocus={(e) => e.preventDefault()}>
                <DropdownMenuItem onSelect={() => onOpen(page)}>
                  <FileText className="size-4" />
                  {t('docs.allDocs.open')}
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => { setTimeout(() => setMoveOpen(true), 0) }}>
                  <FolderInput className="size-4" />
                  {t('docs.move.menuItem')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </span>
        </MoveDocPopover>
      </span>
    </div>
  )
}
